import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SocialLoginButtons from '../components/SocialLoginButtons';
import LoginPromoCard from '../components/LoginPromoCard';
import { trackClickCta, trackLoginError, trackLoginSuccess, trackSelectRole } from '../analytics/analytics';

const checkIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Obtener parámetros de la URL
  const returnUrl = searchParams.get('returnUrl');
  const role = searchParams.get('role');

  // Estado local del rol seleccionado (por defecto comensal si no viene en la URL)
  const [selectedRole, setSelectedRole] = useState<string>(role || 'comensal');

  const handleSelectRole = (newRole: string) => {
    setSelectedRole(newRole);
    trackSelectRole(newRole);
    const params = new URLSearchParams(searchParams);
    if (newRole) {
      params.set('role', newRole);
    } else {
      params.delete('role');
    }
    setSearchParams(params);
  };

  const handleLoginSuccess = () => {
    console.log('✅ Login exitoso');
    trackLoginSuccess('social', selectedRole);
    // Redirigir a la URL de retorno si existe, sino a home
    if (returnUrl) {
      navigate(returnUrl);
    } else {
              navigate('/profile'); // Página Home muestra el bloque de "¡Bienvenido, ...!" si hay usuario
    }
  };

  const handleLoginError = (error: string) => {
    console.error('❌ Error en login:', error);
    trackLoginError(error);
    setIsLoading(false);
  };

  // Si el usuario ya está logueado, mostrar información del perfil
  if (user) {
    // Si el usuario ya está logueado, redirigir automáticamente al perfil
    navigate('/profile');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0d1b26] relative overflow-hidden flex items-center justify-center p-4 sm:p-6">
      {/* Glows y textura de fondo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-48 left-1/4 w-[700px] h-[700px] rounded-full bg-[#22B6B2]/15 blur-[140px]" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full bg-[#2C4358]/60 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, black, transparent)',
          }}
        />
      </div>

      <div className="relative w-full max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 rounded-[2rem] overflow-hidden shadow-2xl shadow-black/40">
          {/* ==================== Panel de marca (desktop) ==================== */}
          <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#101f2c] to-[#2C4358] p-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[350px] h-[350px] rounded-full bg-[#22B6B2]/20 blur-[90px] pointer-events-none" />

            <button
              onClick={() => {
                trackClickCta('ir_a_inicio', 'login_brand_panel', '/');
                navigate('/');
              }}
              aria-label="Ir al inicio"
              className="relative flex items-center gap-3 group w-fit focus:outline-none focus:ring-2 focus:ring-[#22B6B2]/50 rounded-full"
            >
              <img
                src="/assets/ots/nobackground/Servicio_a_Tiempo_icon_64x64.png"
                alt="Servicio a Tiempo"
                className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <span className="text-white font-bold tracking-tight text-lg">
                Servicio <span className="text-[#22B6B2]">a Tiempo</span>
              </span>
            </button>

            <div className="relative">
              <h2 className="text-4xl font-extrabold tracking-tight text-white leading-tight mb-6">
                Tu restaurante,
                <span className="block bg-gradient-to-r from-[#22B6B2] to-[#4fd8d4] bg-clip-text text-transparent pb-1">
                  organizado y a tiempo
                </span>
              </h2>
              <ul className="space-y-4">
                {[
                  'Menús QR interactivos para tus mesas',
                  'Pedidos por WhatsApp sin caos',
                  'Panel completo para tu operación',
                ].map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3 text-[#9db2bf]">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#22B6B2]/15 text-[#5fd6d2] flex-shrink-0">
                      {checkIcon}
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <p className="relative text-sm text-[#7b8b99]">
              © {new Date().getFullYear()} Servicio a Tiempo
            </p>
          </div>

          {/* ==================== Tarjeta de login ==================== */}
          <div className="bg-white p-6 sm:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4 lg:hidden">
                <button
                  onClick={() => navigate('/')}
                  aria-label="Ir al inicio"
                  className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#22B6B2]/50"
                >
                  <img
                    src="/assets/ots/Servicio_a_Tiempo_icon_128x128.png"
                    alt="Servicio a Tiempo"
                    className="w-24 h-24 object-contain"
                  />
                </button>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#2C4358] mb-2">
                ¡Bienvenido!
              </h1>
              <p className="text-[#7b8b99]">
                Inicia sesión para continuar
              </p>
            </div>

            {/* Selector de tipo de acceso */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#7b8b99] mb-3">Elige tu tipo de acceso</p>
              <div className="grid grid-cols-2 gap-2 p-1.5 bg-[#f6f8fa] border border-[#e6ebf0] rounded-2xl" role="tablist" aria-label="Selector de tipo de acceso">
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedRole === 'owner'}
                  onClick={() => handleSelectRole('owner')}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    selectedRole === 'owner'
                      ? 'bg-[#22B6B2] text-white shadow-md shadow-[#22B6B2]/30'
                      : 'text-[#7b8b99] hover:text-[#2C4358]'
                  }`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M3 9l1-5h16l1 5" /><path d="M4 9v11h16V9" /><path d="M9 20v-6h6v6" /><path d="M3 9c0 1.5 1.5 3 3 3s3-1.5 3-3c0 1.5 1.5 3 3 3s3-1.5 3-3c0 1.5 1.5 3 3 3s3-1.5 3-3" />
                  </svg>
                  Soy Restaurante
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={selectedRole === 'comensal'}
                  onClick={() => handleSelectRole('comensal')}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    selectedRole === 'comensal'
                      ? 'bg-[#22B6B2] text-white shadow-md shadow-[#22B6B2]/30'
                      : 'text-[#7b8b99] hover:text-[#2C4358]'
                  }`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2V2" /><path d="M6 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
                  </svg>
                  Soy Comensal
                </button>
              </div>
            </div>

            {/* Login Promo Card */}
            <LoginPromoCard />

            {/* Aviso de privacidad para comensal */}
            {selectedRole === 'comensal' && (
              <div className="mt-5 bg-[#f7fbfb] border border-[#22B6B2]/20 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <img
                    src="/assets/ots/Servicio_a_Tiempo_icon_128x128.png"
                    alt="Servicio a Tiempo"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-sm font-semibold text-[#2C4358]">Aviso de privacidad</span>
                </div>
                <p className="text-sm text-[#4b5563]">
                  Usamos tu correo solo para confirmar pedidos y mejorar tu experiencia. Lee nuestra{' '}
                  <button
                    type="button"
                    onClick={() => {
                      trackClickCta('ver_privacidad', 'login_comensal_notice', '/privacidad');
                      navigate('/privacidad');
                    }}
                    className="text-[#189e9a] font-semibold underline underline-offset-2 hover:text-[#22B6B2]"
                  >
                    Política de Privacidad
                  </button>
                  .
                </p>
              </div>
            )}

            {/* Aceptación de Términos */}
            <div className="mt-5">
              <label
                className={`flex items-start gap-3 text-sm text-[#4b5563] p-4 rounded-2xl border cursor-pointer transition-colors ${
                  accepted ? 'border-[#22B6B2]/40 bg-[#f7fbfb]' : 'border-[#e6ebf0] bg-white hover:border-[#22B6B2]/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#22B6B2] focus:ring-[#22B6B2]"
                />
                <span>
                  He leído y acepto los{' '}
                  <button
                    type="button"
                    onClick={() => {
                      trackClickCta('ver_terminos', 'login_terms', '/terminos');
                      navigate('/terminos');
                    }}
                    className="text-[#189e9a] font-semibold hover:text-[#22B6B2] underline underline-offset-2"
                  >
                    Términos y Condiciones
                  </button>
                  . Para conocer el tratamiento de datos, consulte la{' '}
                  <button
                    type="button"
                    onClick={() => {
                      trackClickCta('ver_privacidad', 'login_terms', '/privacidad');
                      navigate('/privacidad');
                    }}
                    className="text-[#189e9a] font-semibold hover:text-[#22B6B2] underline underline-offset-2"
                  >
                    Política de Privacidad
                  </button>
                  .
                </span>
              </label>
            </div>

            {/* Social Login Buttons */}
            <div className="mt-6">
              <SocialLoginButtons
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                disabled={isLoading || !accepted}
                role={selectedRole}
              />
              {!accepted && (
                <p className="mt-2 text-xs text-center text-[#a3b1bd]">
                  Acepta los términos para continuar
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-xs text-[#a3b1bd]">
                Al continuar, aceptas nuestros{' '}
                <button
                  type="button"
                  onClick={() => {
                    trackClickCta('ver_terminos', 'login_footer', '/terminos');
                    navigate('/terminos');
                  }}
                  className="text-[#189e9a] font-semibold underline underline-offset-2 hover:text-[#22B6B2]"
                >
                  Términos de Servicio
                </button>{' '}
                y{' '}
                <button
                  type="button"
                  onClick={() => {
                    trackClickCta('ver_privacidad', 'login_footer', '/privacidad');
                    navigate('/privacidad');
                  }}
                  className="text-[#189e9a] font-semibold underline underline-offset-2 hover:text-[#22B6B2]"
                >
                  Política de Privacidad
                </button>
              </p>
            </div>

            {/* Skip Login Option */}
            <div className="mt-5 text-center">
              <button
                className="inline-flex items-center gap-1.5 text-[#7b8b99] text-sm font-medium hover:text-[#189e9a] transition-colors"
                onClick={() => {
                  trackClickCta('continuar_sin_login', 'login_footer', '/');
                  navigate('/');
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
                </svg>
                Continuar sin iniciar sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
